/* eslint-disable no-promise-executor-return */

import _ from 'lodash';
import axios from 'axios';
import moment from 'moment';

import * as api from 'assets/js/api';
import store from 'store';

import { LocalStorage } from 'assets/js/auth/localstorage';

// storage points to the current browser window's storage session.
const storage = window.sessionStorage;

// storagePersistent points to the current browser window's persistent storage.
const storagePersistent = LocalStorage();

// COOKIE_DOMAIN is the domain to restrict cookie access to.
const COOKIE_DOMAIN = '.qreserve.com';

// COOKIE_EXPIRES is the number of days for the cookie to expire.
const COOKIE_EXPIRES = 14;

// COOKIE_KEY is the key for browser cookies for our session.
const COOKIE_KEY = 'QRESERVE_SESSION';

// COOKIE_KEY_LEGACY is the legacy key for browser cookies for our session.
const COOKIE_KEY_LEGACY = 'QRESERVE';

// COOKIE_PATH is the path for the cookie.
const COOKIE_PATH = '/';

// LOCAL_STORAGE_KEY is the key for the session data to be stored in persistent
// storage.
const LOCAL_STORAGE_KEY = 'QRESERVE_SESSION';

// PRODUCTION_TOKEN_ERROR_NAME is the error.name used to tell if we're in
// production and should pop a modal telling the user to verify their
// email.
const PRODUCTION_TOKEN_ERROR_NAME = 'NoTokenInProduction';

// SESSION_KEY is the key for the session data to be stored in session storage.
const SESSION_KEY = 'user_token';

// STAY_LOGGED_IN_KEY is the key for the stayLoggedIn boolean to be stored in
// session storage.
const STAY_LOGGED_IN_KEY = 'stay_logged_in';

class Auth {
  constructor(storeArg) {
    this.store = storeArg;

    // Export the constants.
    this.CookieDomain = COOKIE_DOMAIN;
    this.CookieExpires = COOKIE_EXPIRES;
    this.CookieKey = COOKIE_KEY;
    this.CookieKeyLegacy = COOKIE_KEY_LEGACY;
    this.CookiePath = COOKIE_PATH;
    this.ProductionTokenErrorName = PRODUCTION_TOKEN_ERROR_NAME;

    this.computed = {
      auth_user() {
        return this.store.state.auth?.authUser;
      },
      auth_name() {
        return this.store.state.auth?.authUser.display_name;
      },
      auth_roles() {
        return this.store.state.auth?.authUser.roles || [];
      },
    };

    this.methods = {
      // hasRole returns whether or not the authorized user has any roles.
      hasRole: (role) => !this.store.state.auth?.authUser.roles ||
        this.store.state.auth?.authUser.roles.indexOf(role) > -1,
    };
  }

  // GetAuthorizationHeader returns a saved authorization token header if the
  // authorization token exists. Otherwise it returns an empty string.
  GetAuthorizationHeader() {
    const tkn = this.TokenFetch();
    if (!tkn) return '';
    if (!tkn.token) return '';
    return `QReserveToken ${tkn.token}`;
  }

  // GetAuthorizationToken returns a saved authorization token if it exists.
  // Otherwise it returns an empty string.
  GetAuthorizationToken() {
    return _.get(this.TokenFetch(), 'token', '');
  }

  // GetPortalFromAuth gets a portal from the authorization data's list of
  // associated portals.
  GetPortalFromAuth(portalId) {
    const p = _.find(
      this.store.state.auth?.authUser.portals,
      (portal) => portal.portal.portal_id === portalId,
    );
    return p.portal;
  }

  // GetTokenExpiration returns a saved authorization token's expiration.
  // Otherwise it returns an empty string.
  GetTokenExpiration() {
    return _.get(this.TokenFetch(), 'expires', '');
  }

  // InitiatePasswordReset attempts to reset the password through the API.
  // This differs from PasswordReset in that it sends an e-mail to the user
  // and returns the response.
  InitiatePasswordReset(creds) {
    if (!this.store) return null; // Make eslint scream less
    if (creds.email === '') {
      return new Promise((resolve, reject) =>
        reject(Error('Email cannot be blank.')));
    }

    return axios.post(api.urls.INITIATE_PASSWORD_RESET, creds)
      .then((response) => response)
      .catch((err) => {
        throw err.response.data.errors.join();
      });
  }

  // Login logs a user in with their e-mail and password, then sets the
  // relevant authorization variables in the session and state. 'expires' is the
  // duration of time in minutes for the session/cookie to expire. 'membership'
  // is the encoded ID of a federation membership that a user wishes to
  // associate with their QReserve account in the future.
  async Login(creds, expires, membership) {
    if (creds.username === '') {
      throw Error('Email cannot be blank.');
    }
    if (creds.password === '') {
      throw Error('Password cannot be blank.');
    }

    const params = {};
    if (expires) params.expiry = expires;
    if (membership) params.membership = membership;

    const headers = {};
    if (creds.totpCode) headers['X-QR-TOTP'] = creds.totpCode;

    const result = await this.loginAsyncRoutine(
      { auth: creds, params, headers });
    return result;
  }

  // LoginOneTime logs a user in with a special one time token, then sets the
  // relevant authorization variables in the session and state. 'expires' is the
  // duration of time in minutes for the session/cookie to expire. 'membership'
  // is the encoded ID of a federation membership that a user wishes to
  // associate with their QReserve account in the future.
  //
  // It only works in development/testing.
  async LoginOneTime(token) {
    if (_.isNull(token)) {
      const err = Error('Production one time login disabled.');
      err.name = this.ProductionTokenErrorName;
      throw err;
    }

    if (token === '') {
      throw Error('Token cannot be blank.');
    }

    const headers = { Authorization: `QReserveOneTime ${token}` };
    const result = await this.loginAsyncRoutine({ headers });
    return result;
  }

  // LoginWithSavedAuth uses a saved authorization token to log the user in if
  // they have timed out. In the case there is no stored token, they are simply
  // logged out.
  // @expiresOverride: Moment object for the new expiry time.
  // @force: Force negotiating a new token even if logged in.
  async LoginWithSavedAuth({ expiresOverride = null }) {
    // Check for current authentication and do it all synchronously (with
    // Mutations) because we want to wait until this is done before continuing
    // on with the application.
    if (this.store.state.auth.authChecked) {
      return;
    }

    let loginResp = this.TokenFetch();
    if (loginResp) {
      if (!this.store.state.auth.jwt || !this.store.state.auth.expires) {
        await this.store.commit('SET_AUTH_TOKEN', loginResp);
      }

      let expires = moment(loginResp.expires);
      if (expiresOverride) expires = expiresOverride;

      try {
        loginResp = await api.GenerateFreshJWT(expires);
      } catch (err) {
        this.TokenPurge();
        await this.store.commit('DO_LOGOUT');
        throw Error('Unexpected error while attempting to negotiate ' +
          `new session token ${String(err)}`);
      }

      await this.store.commit('DO_LOGIN', loginResp);

      try {
        const response = await api.Get(api.urls.SELF);
        await this.store.commit('SET_AUTH_USER', response.data);
      } catch (err) {
        throw Error(err.response.data.errors.join());
      }
    } else {
      await this.store.commit('DO_LOGOUT');
    }
  }

  // LoginWithToken logs the user in with a token, containing the JWT and
  // expires string.
  async LoginWithToken(jwt, expires) {
    const loggedIn = {
      token: jwt,
      expires,
    };

    this.TokenStore(loggedIn);
    await this.store.commit('DO_LOGIN', loggedIn);
    const selfResponse = await api.Get(api.urls.SELF);
    this.store.dispatch('SetAuthUser', selfResponse.data);
  }

  // loginAsyncRoutine is a simple login routine using valid headers.
  async loginAsyncRoutine(config) {
    const localConfig = _.cloneDeep(config);
    let response;

    if (localConfig.auth) {
      // If auth is passed in with a username and password then
      // we make a POST request with it as the auth object already
      // contains a username and password. The qrauth paramter
      // tells the API that this is an authentication request.
      const data = {
        qrauth: true,
        username: localConfig.auth.username,
        password: localConfig.auth.password,
      };

      if (localConfig.auth.totpCode) {
        data.totp_code = localConfig.auth.totpCode;
      }
      if (localConfig.auth.webauthnCredentials) {
        data.webauthn_credential = localConfig.auth.webauthnCredentials;
      }

      // Remove the auth parameter.
      delete localConfig.auth;
      response = await axios.post(api.urls.LOGIN, data, localConfig);
    } else {
      // Handle other login requests that are not using auth.
      // Most likely a token.
      response = await axios.get(api.urls.LOGIN, localConfig);
    }

    // The web session storage is just a k->v store, we need to turn this
    // into a JSON string so that it might properly get stored.
    this.TokenStore({
      expires: response.data.data.expires,
      token: response.data.data.token,
    });
    await this.store.commit('DO_LOGIN', response.data.data);

    const selfResponse = await api.Get(api.urls.SELF);
    this.store.dispatch('SetAuthUser', selfResponse.data);
  }

  // Logout logs out the current user.
  Logout(router, redirectorURL) {
    this.TokenPurge();

    if (redirectorURL) {
      window.location.replace(redirectorURL);
    }

    if (router) {
      if (this.store.state.theme && this.store.state.theme.key) {
        router.Navigate.LoginWithTheme(this.store.state.theme.key);
      } else {
        router.Navigate.Login();
      }
    }
  }

  // PasswordReset resets a password to the given new password that the
  // authorized user pushes through the API.
  PasswordReset(creds) {
    if (!this.store) return null; // Make eslint scream less
    if (creds.password === '') {
      return new Promise((resolve, reject) =>
        reject(Error('Password cannot be blank.')));
    }

    return axios.post(api.urls.PASSWORD_RESET, creds)
      .then().catch((err) => {
        throw err.response.data.errors.join();
      });
  }

  async Signup({ email, password }, themeId) {
    if (email === '') {
      return new Promise((resolve, reject) =>
        reject(Error('Email cannot be blank.')));
    }
    if (password === '') {
      return new Promise((resolve, reject) =>
        reject(Error('Password cannot be blank.')));
    }

    const params = { email, password };

    if (themeId) params.theme = themeId;

    const response = await axios.post(api.urls.CREATE_USER, params);
    const token = _.get(response, 'data.data.onetimetoken', null);

    return this.LoginOneTime(token);
  }

  // StayLoggedInFetch fetches the stayLoggedIn boolean from session storage.
  StayLoggedInFetch() {
    if (!this.store) return null; // Make eslint scream less
    if (storage) return Boolean(storage.getItem(STAY_LOGGED_IN_KEY));
    return false;
  }

  // StayLoggedInStore puts the stayLoggedIn boolean in session storage.
  StayLoggedInStore(val) {
    if (!this.store) return; // Make eslint scream less
    if (storage && val) storage.setItem(STAY_LOGGED_IN_KEY, '1');
    if (storage && !val) storage.setItem(STAY_LOGGED_IN_KEY, '');
  }

  // TokenFetch fetches a token from the persistent storage.
  TokenFetch() {
    let tokenJSON;
    if (storage) tokenJSON = storage.getItem(SESSION_KEY);
    if (!tokenJSON && storagePersistent) {
      tokenJSON = storagePersistent.getItem(LOCAL_STORAGE_KEY);

      // Hack to discover if the user wanted to stay logged in or not.
      if (tokenJSON) this.StayLoggedInStore(true);
    }
    if (!tokenJSON) return null;
    return JSON.parse(tokenJSON);
  }

  // TokenPurge purges a session token from the session and persistent storage.
  TokenPurge() {
    if (!this.store) return; // Make eslint scream less
    if (storage) storage.removeItem(SESSION_KEY);
    if (storagePersistent) storagePersistent.removeItem(LOCAL_STORAGE_KEY);
  }

  // TokenPurgeIfExpired removes a token from the persistent storage if it's
  // expired. Returns true if the token was expired, otherwise returns false.
  TokenPurgeIfExpired() {
    if (!this.store) return false; // Make eslint scream less
    let tokenJSON;
    if (storage) tokenJSON = storage.getItem(SESSION_KEY);
    if (!tokenJSON && storagePersistent) {
      tokenJSON = storagePersistent.getItem(LOCAL_STORAGE_KEY);
    }

    if (!tokenJSON) return false;
    const token = JSON.parse(tokenJSON);
    const currentTime = new Date().getTime();
    if (currentTime > moment(token.expires).valueOf()) {
      if (storage) storage.removeItem(SESSION_KEY);
      if (storagePersistent) storagePersistent.removeItem(LOCAL_STORAGE_KEY);
      return true;
    }

    return false;
  }

  // TokenStore inserts a token into the persistent storage.
  TokenStore(token) {
    if (storage) storage.setItem(SESSION_KEY, JSON.stringify(token));
    if (storagePersistent && this.StayLoggedInFetch()) {
      storagePersistent.setItem(LOCAL_STORAGE_KEY, JSON.stringify(token));
    }
  }
}

const authInstance = new Auth(store);
// eslint-disable-next-line
export { authInstance as default };
